import _ from 'lodash';

import Joi from 'joi';
import PropTypes from 'prop-types';

import Button from '../../themed/button';
import Form from '../../common/form';

import { getAffirmativeOptions, getDefectTypes, getResultOptions } from '../../../utils/data';
import { getDateFromString, getFileUploadTypes, matchPhrases } from '../../../utils/utilities';
import { IMAGE_LIMITS, IMAGE_TYPE_SELECTION, IQI_TYPES, MATERIAL_OPTIONS, RT_TECHNIQUES, SDD_OPTIONS, SOURCE_ACTIVITY, SOURCE_MANUFACTURERS, SOURCES } from '../../../utils/constants';

import toastService from '../../../services/toastService';

class EditDRTReportForm extends Form {
	state = {
		data: {
			id: 0,
			weldNumber: '',
			shotNumber: '',
			thickness: 0,
			pipeDia: 0,
			defect: '',
			defectType: '',
			result: '',
			defectLength: '',
			source: '',
			sourceActivity: '',
			sourceSize: '',
			contrastUsed: '',
			rtTechnique: '',
			sdd: '',
			odd: '',
			iqi: '',
			iqiType: '',
			iqiSpecification: '',
			pipeSpecification: '',
			imageFilmType: '',
			sourceManufacturer: '',
			pipeMaterialClass: '',
			drtScannerModalName: '',
			serialNumber: '',
			equipmentSerialNumber: '',
			inspectionDate: new Date(),
			drtScannerCalibrationDate: new Date(),
			sourceConformityInspectionDate: new Date(),
			limitation: '',
			image: []
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		id: Joi.number().required().label('ID'),
		weldNumber: Joi.string().min(3).required().label('Weld Number'),
		shotNumber: Joi.string().min(3).required().label('DRT Shot Number'),
		thickness: Joi.number().positive().required().label('Thickness (mm)'),
		pipeDia: Joi.number().positive().required().label('Pipe Dia (Inch)'),
		defect: Joi.string().min(3).required().label('Defect'),
		defectLength: Joi.alternatives().try(Joi.string().valid('N/A').insensitive(), Joi.number().positive()).required().label('Defect Length (mm)'),
        defectType: Joi.alternatives().try(Joi.array().min(1).items(Joi.object({
			id: Joi.string().required(),
			name: Joi.string().required(),
		})), Joi.string().min(3)).required().label('Defect Type'),
		result: Joi.string().min(3).required().label('Result'),
		source: Joi.string().min(3).required().label('Source'),
		sourceActivity: Joi.string().min(3).required().label('Source Activity'),
		sourceSize: Joi.string().min(3).required().label('Source Size'),
		contrastUsed: Joi.string().min(3).required().label('Contrast Used'),
		sdd: Joi.number().min(1).required().label('SDD (cm)'),
		odd: Joi.number().min(1).required().label('ODD (cm)'),
		iqi: Joi.string().min(3).required().label('IQI'),
		iqiType: Joi.string().min(3).required().label('IQI Type'),
		iqiSpecification: Joi.string().min(3).required().label('IQI Specification'),
		pipeSpecification: Joi.string().min(3).required().label('Pipe Specification'),
		rtTechnique: Joi.string().min(3).required().label('RT Technique'),
		imageFilmType: Joi.string().min(3).required().label('Image Type'),
		sourceManufacturer: Joi.string().required().label('Source Manufacturer'),
		pipeMaterialClass: Joi.string().required().label('Pipe Material Class'),
		drtScannerModalName: Joi.string().min(3).required().label('DRT Scanner Name'),
		serialNumber: Joi.string().min(3).required().label('Serial Number'),
		equipmentSerialNumber: Joi.string().required().label('Source Serial Number'),
		inspectionDate: Joi.date().less('now').required().label('Inspection Date'),
		drtScannerCalibrationDate: Joi.date().greater('now').required().label('DRT Scanner Calibration Due Date'),
		sourceConformityInspectionDate: Joi.date().greater('now').required().label('Source Conformity Inspection Date'),
		limitation: Joi.string().optional().label('Limitation'),
		image: Joi.any().when('result', {
			is: 'Restrictions',
			then: Joi.array().min(0).max(IMAGE_LIMITS.DRT_PHOTO).required().label('Images'),
			otherwise: Joi.array().min(1).max(IMAGE_LIMITS.DRT_PHOTO).required().label('Images')
		}),
	};

	static propTypes = {
		onEdit: PropTypes.func,
		onDeleteItem: PropTypes.func,
		reportItem: PropTypes.object.isRequired,
	};

	componentDidMount(): void {
		if (this.props.reportItem) {
			const initialReportItem = { ..._.omit(this.props.reportItem, 'acceptanceStd') };
			
			if (initialReportItem.calibrationDueDate) {
				if (_.isString(initialReportItem.calibrationDueDate)) {
					const dueDate = getDateFromString(initialReportItem.calibrationDueDate);

					if (!initialReportItem.drtScannerCalibrationDate) initialReportItem.drtScannerCalibrationDate = dueDate;
					if (!initialReportItem.sourceConformityInspectionDate) initialReportItem.sourceConformityInspectionDate = dueDate;

					initialReportItem.calibrationDueDate = undefined;
				}
			}

			if (!initialReportItem.image) {
				initialReportItem.image = [];
			}

			if (matchPhrases(initialReportItem.result, 'rejected') && _.isString(initialReportItem.defectType)) {
                initialReportItem.defectType = initialReportItem.defectType.split(', ').map((value: string) => ({ id: value, name: value }));
            }

			this.setState({ data: initialReportItem });
		}
	}

	componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
		if (prevState.data.result === this.state.data.result) return;
    
        const data = { ...this.state.data } as any;

        if (this.state.data.result && _.isString(this.state.data.defectType)) {
			if (matchPhrases(this.state.data.result, 'rejected')) {
				data.defectType = data.defectType.split(', ').map((value: string) => ({ id: value, name: value }));
			}

			if (this.getResultIsRestriction()) {
				data.defect = 'N/A';
				data.defectType = 'N/A';
				data.defectLength = 'N/A';
			}
        }

        this.setState({ data });
	}

	getApiData = () => {
        if (_.isArray(this.state.data.defectType)) {
            const defectType = this.state.data.defectType.map((option) => option.id).join(', ');
            return { ...this.state.data, defectType };
        }

        return this.state.data;
    };

	getResultIsRestriction = () => {
		return matchPhrases(this.state.data.result, 'restrictions');
	};

	doSubmit = (): void => {
		this.props.onEdit(this.getApiData());
		toastService.success('Changes saved successfully!');
	};

	renderDefectType = () => {
        if (this.state.data.result && matchPhrases(this.state.data.result, 'rejected')) {
            return this.renderMultiSelect({
                name: 'defectType',
                label: 'Defect Type',
                placeholder: 'Select Defect Type',
                showCheckbox: true,
				loading: false,
                options: getDefectTypes().map((type) => ({ id: type, name: type })),
            });
        }

        return this.renderInput({
            type: 'text',
            name: 'defectType',
            label: 'Defect Type',
            placeholder: 'Enter Defect Type',
			disabled: this.getResultIsRestriction(),
        });
    };

	renderSourceActivityUnit = () => {
		const source = SOURCES.find((source) => source.unit === this.state.data.source);
		return source ? `(${source.unit})` : '';
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'weldNumber',
							label: 'Weld Number',
							placeholder: 'Enter Weld Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'shotNumber',
							label: 'DRT Shot Number',
							placeholder: 'Enter DRT Shot Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'thickness',
							label: 'Thickness (mm)',
							placeholder: 'Enter Thickness (mm)',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'pipeDia',
							label: 'Pipe Dia (Inch)',
							placeholder: 'Enter Pipe Dia (Inch)',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: getAffirmativeOptions(),
							name: 'defect',
							label: 'Defect',
							placeholder: 'Enter Defect',
							disabled: this.getResultIsRestriction(),
						})}
					</div>

					<div className="col">
						{this.renderDefectType()}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'result',
							label: 'Result',
							placeholder: 'Select Result',
							options: getResultOptions(),
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'defectLength',
							label: 'Defect Length (mm)',
							placeholder: 'Enter Defect Length (mm)',
							disabled: this.getResultIsRestriction(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: SOURCES,
							name: 'source',
							label: 'Source',
							placeholder: 'Enter Source',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: SOURCE_ACTIVITY,
							name: 'sourceActivity',
							label: 'Source Activity ' + this.renderSourceActivityUnit(),
							placeholder: 'Enter Source Activity',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sourceSize',
							label: 'Source Size (mm)',
							placeholder: 'Enter Source Size',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: RT_TECHNIQUES,
							name: 'rtTechnique',
							label: 'RT Technique',
							placeholder: 'Enter RT Technique',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: SDD_OPTIONS,
							name: 'sdd',
							label: 'SDD (cm)',
							placeholder: 'Select SDD Value',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: SDD_OPTIONS,
							name: 'odd',
							label: 'ODD (cm)',
							placeholder: 'Select ODD Value',
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'iqi',
							label: 'IQI',
							placeholder: 'Enter IQI Value',
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							options: IQI_TYPES,
							name: 'iqiType',
							label: 'IQI Type',
							placeholder: 'Select IQI Value',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'iqiSpecification',
							label: 'IQI Specification',
							placeholder: 'Enter IQI Specification',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: MATERIAL_OPTIONS,
							name: 'pipeSpecification',
							label: 'Material Specification',
							placeholder: 'Enter Material Specification',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: IMAGE_TYPE_SELECTION,
							name: 'imageFilmType',
							label: 'Image Type',
							placeholder: 'Select Image Type',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: getAffirmativeOptions(),
							name: 'contrastUsed',
							label: 'Contrast Used',
							placeholder: 'Enter Contrast Used',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: SOURCE_MANUFACTURERS,
							name: 'sourceManufacturer',
							label: 'Source Manufacturer',
							placeholder: 'Enter Source Manufacturer',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'pipeMaterialClass',
							label: 'Pipe Material Class',
							placeholder: 'Enter Pipe Material Class',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'equipmentSerialNumber',
							label: 'Source Serial Number',
							placeholder: 'Enter Source Serial Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'drtScannerModalName',
							label: 'DRT Scanner Name',
							placeholder: 'Enter DRT Scanner Name',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'serialNumber',
							label: 'DRT Scanner Serial Number',
							placeholder: 'Enter DRT Scanner Serial Number',
						})}
					</div>

					<div className="col">
						{this.renderDatePicker({
							name: 'inspectionDate',
							label: 'Inspection Date',
							placeholderText: 'Select Inspection Date',
							maxDate: new Date(),
						})}
					</div>

					<div className="col">
						{this.renderDatePicker({
							name: 'drtScannerCalibrationDate',
							label: 'DRT Scanner Calibration Due Date',
							placeholderText: 'DRT Scanner Calibration Due Date',
							minDate: new Date(),
						})}
					</div>
					
					<div className="col">
						{this.renderDatePicker({
							name: 'sourceConformityInspectionDate',
							label: 'Source Conformity Inspection Date',
							placeholderText: 'Source Conformity Inspection Date',
							minDate: new Date(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				{this.renderTextArea({
					name: 'limitation',
					placeholder: 'Enter Limitation (If any)',
					label: 'Limitation (If any)',
					rows: '6',
				})}

				<div className="my-4 divider"></div>

				{this.renderFileInput({
					max: IMAGE_LIMITS.DRT_PHOTO,
					multiple: IMAGE_LIMITS.DRT_PHOTO > 1,
					name: 'image',
					accept: 'image/*,application/pdf',
					label: 'DRT Image',
					sublabel: 'Upload DRT Photo',
					types: getFileUploadTypes(),
					enforceImageCaptionUpdate: false
				})}

				<div className="my-4 divider"></div>

				<div className="d-flex justify-content-start">
					{this.renderButton('Update Item')}

					<div className="mx-4">
						<Button
							label="Delete"
							icon="trash"
							style={{ backgroundColor: 'red' }}
							onClick={this.props.onDeleteItem}
						/>
					</div>
				</div>
			</form>
		);
	}
}

export default EditDRTReportForm;
